import React, { ChangeEvent, FC, useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers'
import { ISelectOption } from 'components/forms/Selects'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { useValidationFormContext } from 'utils/context/ValidationFormContext'
import * as yup from 'yup'
import { CarrierSelect } from 'apps/fleet-planner/components/atoms'
import { DeactivatedDriverToolTip } from 'apps/fleet-planner/components/drivers/DeactivatedDriverTooltip'
import { DriverStatusTag } from 'apps/fleet-planner/components/drivers/DriverStatusTag'
import { DriverUpdatesToggle } from 'apps/fleet-planner/components/drivers/DriverUpdatesToggle'
import {
  AddEntityForm,
  AddEntityFormFooter,
} from 'apps/fleet-planner/components/forms/AddEntityForm'
import { IAddNewEntityFormProps } from 'apps/fleet-planner/components/forms/TrailerForm'
import { FP_FAQ_URL_DRIVER_LED_STATUS } from 'apps/fleet-planner/config/constants'
import { useMembershipContext } from 'apps/fleet-planner/context/MembershipContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IUseTeamMemberApiState } from 'apps/fleet-planner/hooks/useTeamMembersApi'
import { DriverSchemaType } from 'apps/fleet-planner/schema/DriverSchema'
import { isDriverPhoneRequired } from 'apps/fleet-planner/schema/utils'
import { DriverSettingsType, DriverStatusType, IDriver } from 'apps/fleet-planner/types'
import { selectCarrierOptionList, selectCarriersList } from 'apps/fleet-planner/utils/selectors'
import { TextInput } from 'components/atoms'
import {
  PhoneInput,
  INLINE_MESSAGE_VARIANTS,
  InlineMessage,
  Button,
  brandColors,
  Tag,
  TAG_STYLE,
  TAG_SIZE,
} from 'transfix-ui'
import { formatPhone, unformatPhone } from 'utils/formatPhone'
import { DriverOptedOutMessage } from '../driver-led-status/DriverOptedOutMessage'

const useStyles = makeStyles(({ spacing }) => ({
  smsUpdatesContainer: {
    backgroundColor: brandColors.coolGray1,
    display: 'grid',
    gap: spacing(3),
    padding: spacing(4),
    margin: spacing(4, 0),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    marginBottom: spacing(1),
  },
}))

const requiredMessage = 'This field is required.'
const alreadyInUseMessage = 'This phone number is already in use.'

interface NewDriverSchemaContext {
  /**
   * Function used to see if a user has already been created with
   * a cellphone value
   */
  getUserByCellPhone?: IUseTeamMemberApiState['getUserByCellPhone']
  /**
   * Checks if it's a TFX load so we can require a driver's cell phone
   */
  isExternal?: boolean
}

// More strictly enforces schema from DriverSchema.ts!
export const NewDriverSchema = yup
  .object<IDriver>({
    firstName: yup.string().required('Please enter first name.'),
    lastName: yup.string().required('Please enter last name.'),
    motorCarrier: yup
      .object({
        id: yup.number(),
        name: yup.string(),
      })
      .required(requiredMessage),
    email: yup.string().email('Please enter a valid email').notRequired(),
    cellPhone: yup
      .string()
      .test('is-phone-required', 'Please enter a cellphone number', isDriverPhoneRequired)
      .test('phone-already-in-use', alreadyInUseMessage, function isNewPhoneNumber(val: string) {
        const { context } = this.options as { context: NewDriverSchemaContext }
        const { getUserByCellPhone } = context || {}

        // If this is not set in parent context, don't validate
        if (!getUserByCellPhone || typeof getUserByCellPhone !== 'function') {
          return true
        }

        return !getUserByCellPhone(val)
      }),
    settings: yup.object<DriverSettingsType>({
      smsStatusUpdatesEnabled: yup.boolean().required(),
    }),
  })
  .required()

type NewDriverSchemaType = yup.InferType<typeof NewDriverSchema>

export interface IDriverFormProps extends IAddNewEntityFormProps<DriverSchemaType> {
  isEditMode?: boolean
  /**
   * @default false
   */
  isExternal?: boolean
  /**
   * Triggers an inline error on the cellPhone field when the email and cellphone number match
   * different accounts.
   * @default false
   */
  hasAccountConflict?: boolean
}

const emptyDriverState: DriverSchemaType = {
  firstName: '',
  lastName: '',
  motorCarrier: {
    id: 0,
    name: '',
  },
  email: '',
  cellPhone: '',
  status: DriverStatusType.active,
  settings: {
    smsStatusUpdatesEnabled: false,
    suppressSmsStatusUpdatesOptIn: false,
  },
  cellPhoneIsTwilioSubscribed: false,
}

/**
 * @requires MembershipContext to validate that current user is able to add new drivers
 * @requires ValidationFormContext for sharing validation state with parent component
 */
export const DriverForm: FC<IDriverFormProps> = ({
  onSuccess: onSuccessProp,
  onCancel: onCancelProp = () => {},
  defaultValues: defaultValuesProp = {},
  primaryButtonText: primaryButtonTextProp = 'Add Driver',
  isLoading: isLoadingProp,
  isEditMode: isEditModeProp = false,
  isExternal = false,
  hasAccountConflict = false,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const carriersOptions = useSelector(selectCarrierOptionList)
  const carriers = useSelector(selectCarriersList)

  const [firstCarrier] = carriers || []
  const { id, name } = firstCarrier || {}
  const { setHasErrors } = useValidationFormContext()
  const { getUserByCellPhone } = useMembershipContext()

  // If there's only one motorCarrier, select that by default
  const defaultMotorCarrier = carriers?.length === 1 ? { id, name } : undefined
  const defaultDriverSettings = {
    smsStatusUpdatesEnabled: defaultValuesProp?.settings?.smsStatusUpdatesEnabled ?? false,
    suppressSmsStatusUpdatesOptIn:
      defaultValuesProp?.settings?.suppressSmsStatusUpdatesOptIn ?? false,
  }

  const defaultVal = {
    firstName: defaultValuesProp?.firstName ?? '',
    lastName: defaultValuesProp?.lastName ?? '',
    email: defaultValuesProp?.email ?? '',
    motorCarrier: defaultValuesProp.motorCarrier ?? defaultMotorCarrier,
    cellPhone: defaultValuesProp?.cellPhone ?? '',
    status: defaultValuesProp?.status ?? DriverStatusType.active,
    settings: defaultDriverSettings,
    cellPhoneIsTwilioSubscribed: defaultValuesProp?.cellPhoneIsTwilioSubscribed ?? true,
  }

  const showInlineErrorMessage =
    !defaultVal.cellPhoneIsTwilioSubscribed && defaultVal.settings.smsStatusUpdatesEnabled

  const { control, errors, handleSubmit, setError } = useForm<
    NewDriverSchemaType,
    NewDriverSchemaContext
  >({
    mode: 'all',
    resolver: yupResolver(NewDriverSchema),
    context: {
      getUserByCellPhone,
      isExternal,
    },
    defaultValues: NewDriverSchema.cast({
      ...emptyDriverState,
      ...defaultVal,
    }),
  })

  useEffect(() => {
    const hasErrors = Object.keys({ ...errors }).length !== 0
    if (hasErrors) setHasErrors(hasErrors)
  }, [errors])

  // triggers inline error if hasAccountConflict is set
  useEffect(() => {
    if (hasAccountConflict) {
      setError('cellPhone', {
        type: 'manual',
        shouldFocus: true,
        message: 'This cellphone number is already in use. Please enter another one.',
      })
    }
  }, [hasAccountConflict])

  const submitHandler = (data: DriverSchemaType) => {
    if (
      // if smsStatusUpdatesEnabled is enabled and was previousy disabled, track the event on Mixpanel
      data.settings?.smsStatusUpdatesEnabled &&
      !defaultValuesProp.settings?.smsStatusUpdatesEnabled
    ) {
      trackEvent('Driver Led Status', 'DRIVER_LED_STATUS_DRIVERS_PAGE_UPDATES_ON')
    }
    onSuccessProp(data)
  }

  const settings = useWatch<DriverSchemaType['settings']>({
    name: 'settings',
    control,
    defaultValue: {
      smsStatusUpdatesEnabled: false,
    },
  })

  const { smsStatusUpdatesEnabled = false } = settings || {}
  const { header, smsUpdatesContainer } = useStyles()

  return (
    <AddEntityForm dataTest='add-new-driver-form'>
      {/* First Name */}
      <div className='mb4'>
        <Controller
          name='firstName'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                label='First Name'
                onChange={changeHandler}
                value={value}
                onBlur={onBlur}
                large
                required
                autoFocus
                validationMessage={errors?.firstName?.message}
                invalid={!!errors?.firstName?.message}
                placeholder='Henry'
                dataTest='driver-firstName-input'
              />
            )
          }}
        />
      </div>
      {/* Last name */}
      <div className='mb4'>
        <Controller
          name='lastName'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                label='Last Name'
                onChange={changeHandler}
                value={value}
                onBlur={onBlur}
                large
                required
                validationMessage={errors?.lastName?.message}
                invalid={!!errors?.lastName?.message}
                placeholder='Ford'
                dataTest='driver-lastName-input'
              />
            )
          }}
        />
      </div>
      {/* Motor Carrier Number */}
      <div className='mb4'>
        <Controller
          name='motorCarrier'
          control={control}
          render={({ onChange, value }) => {
            const changeHandler = ({ value: motorCarrierId }: ISelectOption<number>) => {
              const matchingCarrier = carriers.find(c => c.id === motorCarrierId)
              onChange({
                id: matchingCarrier?.id,
                name: matchingCarrier?.name,
              })
            }

            return (
              <CarrierSelect
                disabled={!!defaultValuesProp.motorCarrier}
                large
                label='Employer'
                fetchList={!carriersOptions.length}
                motorCarrierId={value?.id}
                onChange={changeHandler}
                required
                selectDefaultOnMount={false}
                validationMessage={errors?.id?.message}
                invalid={!!errors?.id?.message}
              />
            )
          }}
        />
      </div>
      {/* Email */}
      <div className='mb4'>
        <Controller
          name='email'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                label='Contact Email'
                onChange={changeHandler}
                value={value}
                onBlur={onBlur}
                large
                placeholder='henry@ford.com'
                dataTest='driver-email-input'
                validationMessage={errors?.email?.message}
                invalid={!!errors?.email?.message}
                className='fs-mask'
              />
            )
          }}
        />
      </div>
      {/* Cell Phone */}
      <div className={smsUpdatesContainer}>
        <div>
          <div className={header}>
            <Typography variant='h6'>Enable SMS Prompt to Ask for Status Updates</Typography>
            <Tag tagStyle={TAG_STYLE.success} size={TAG_SIZE.xsmall} label='Recommended' />
          </div>
          <Typography>
            We&apos;ll automatically text your driver a timely reminder at every stop to update
            their status. You&apos;ll receive an email when they arrive and depart each pickup and
            dropoff. If your driver tracks well or if you opt them out, we won&apos;t send a message
            and will just keep their phone number on file.{' '}
            <a href={FP_FAQ_URL_DRIVER_LED_STATUS} target='_blank' rel='noopener noreferrer'>
              <strong>Learn More</strong>
            </a>
          </Typography>
        </div>
        <Controller
          name='cellPhone'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const formattedValue = formatPhone(value, { removeCountryCode: '+1' })
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(unformatPhone(e.target.value))
            }

            return (
              <PhoneInput
                className='col-md-5 pl0 fs-mask'
                label='Mobile Phone'
                onChange={changeHandler}
                onBlur={onBlur}
                placeholder='(   )     -     '
                value={formattedValue}
                validationMessage={errors?.cellPhone?.message}
                invalid={!!errors?.cellPhone?.message}
                dataTest='driver-cellphone-input'
                clearable={false}
                required={smsStatusUpdatesEnabled || isExternal}
              />
            )
          }}
        />
        {/* Driver opt-in toggle */}
        <Controller
          name='settings'
          control={control}
          render={({ onChange, value }) => {
            const changeHandler = (driverSettings: DriverSettingsType) => {
              onChange(driverSettings)
            }
            return (
              <DriverUpdatesToggle
                driverSettings={value}
                onChangeHandler={changeHandler}
                isDeactivated={!defaultVal.cellPhoneIsTwilioSubscribed}
              />
            )
          }}
        />
      </div>
      {errors?.cellPhone?.message === alreadyInUseMessage && (
        <Box mb={2}>
          <InlineMessage
            type={INLINE_MESSAGE_VARIANTS.ALERT}
            message={
              <>
                <strong>A driver with this phone number already exists.</strong> To add them to a
                different carrier, you can edit their permissions on the{' '}
                <Link to='/team'>Team page</Link>.
              </>
            }
          />
        </Box>
      )}
      {showInlineErrorMessage && (
        <InlineMessage
          className='mb4 col-md'
          type={INLINE_MESSAGE_VARIANTS.ALERT}
          message={DriverOptedOutMessage()}
        />
      )}
      {/* Status -> Edit mode only */}
      {isEditModeProp && (
        <div className='mb4 col-md'>
          <Controller
            name='status'
            control={control}
            render={({ onChange, value }) => {
              const changeHandler = (option: ISelectOption) => {
                onChange(option.value)
              }
              return (
                <>
                  <div className='b text-small text-secondary mb2'>Status</div>
                  <DriverStatusTag driverStatus={value} onSelectHandler={changeHandler} />
                  <DeactivatedDriverToolTip isDriversModal className='ml4' />
                </>
              )
            }}
          />
        </div>
      )}
      <AddEntityFormFooter>
        <Button color='tertiary' size='large' onClick={onCancelProp} data-test='cancel-new-driver'>
          Cancel
        </Button>
        <Button
          color='primary'
          disabled={isLoadingProp}
          size='large'
          type='submit'
          data-test='save-new-driver'
          onClick={handleSubmit(submitHandler)}>
          {primaryButtonTextProp}
        </Button>
      </AddEntityFormFooter>
    </AddEntityForm>
  )
}
